import { realtimeApi } from '@/realtime/api'
import { onReconnected } from '@/realtime/echo'
import {
  LOAD_OLDER_THRESHOLD_PX,
  RECONNECT_JITTER_MS,
  confirmMessage,
  mergeFetched,
  mergeMessages,
  newestId,
  oldestId,
  sendFailure,
  sortMessages,
  updateLocal,
  upsertMessage,
} from '@/realtime/chatMessages'

/**
 * La lista de mensajes de un chat abierto, compartida por el chat 1 a 1
 * (ChatConversation) y el de sala (EventRoomContentElementChatChannel).
 *
 * El componente define `chanel_id`, `messages`, el ref `scrollToMe` (el
 * contenedor con scroll), scrollToElement() y storeOwnMessage(local), que
 * guarda un mensaje propio y devuelve el que guardó el backend.
 *
 * Los mensajes se cargan por páginas: al abrir, los últimos 50; al subir con
 * el scroll, la página anterior; al reconectarse, solo lo posterior al último
 * que se tenía. Ver ShowChannelMessagesController en el backend.
 */
export default {
  data: () => ({
    // Hay mensajes más viejos que los cargados.
    hasOlder: false,
    loadingOlder: false,
  }),
  mounted() {
    // Lo que se escribió mientras la conexión estaba caída no llegó por
    // MessageSent: se pide al reconectarse.
    try {
      this.stopReconnected = onReconnected(() => {
        setTimeout(this.refetchMissed, Math.random() * RECONNECT_JITTER_MS)
      })
    } catch (e) {
      // Sin tiempo real el chat igual se carga al abrirlo.
    }
  },
  beforeDestroy() {
    if (this.stopReconnected) {
      this.stopReconnected()
    }
  },
  methods: {
    /**
     * Los últimos mensajes del chat, al abrirlo. Reemplaza lo que había.
     */
    async loadLatest(channelId) {
      const page = await realtimeApi.getChannelMessages(channelId)
      this.messages = sortMessages(page.messages)
      this.hasOlder = page.has_more
      return page
    },
    handleNewMessage(message) {
      this.messages = upsertMessage(this.messages, message)
      this.$nextTick(() => {
        this.scrollToElement()
      })
    },
    async refetchMissed() {
      const channelId = this.chanel_id
      if (this._isDestroyed || !channelId) {
        return
      }
      try {
        const after = newestId(this.messages)
        const page =
          after === null
            ? null
            : await realtimeApi.getChannelMessages(channelId, { after })
        // Cambió de chat mientras esperaba: lo que llegó es de otro.
        if (this.chanel_id !== channelId) {
          return
        }
        if (page && !page.has_more_after) {
          this.messages = mergeMessages(this.messages, page.messages)
          return
        }
        // No tenía nada, o se perdió demasiado: la última página entera.
        const latest = await realtimeApi.getChannelMessages(channelId)
        if (this.chanel_id !== channelId) {
          return
        }
        this.messages = mergeFetched(this.messages, latest.messages)
        this.hasOlder = latest.has_more
      } catch (e) {
        // Se queda con lo que tenía; la próxima reconexión vuelve a intentar.
      }
    },
    onMessagesScroll(event) {
      if (event.target.scrollTop < LOAD_OLDER_THRESHOLD_PX) {
        this.loadOlder()
      }
    },
    async loadOlder() {
      const channelId = this.chanel_id
      const before = oldestId(this.messages)
      if (!this.hasOlder || this.loadingOlder || before === null) {
        return
      }
      this.loadingOlder = true
      const el = this.$refs.scrollToMe
      const previousHeight = el ? el.scrollHeight : 0
      const previousTop = el ? el.scrollTop : 0
      try {
        const page = await realtimeApi.getChannelMessages(channelId, {
          before,
        })
        if (this.chanel_id !== channelId) {
          return
        }
        this.messages = mergeMessages(this.messages, page.messages)
        this.hasOlder = page.has_more
        // Lo agregado arriba empuja el contenido: se compensa para que lo que
        // estaba leyendo no salte.
        await this.$nextTick()
        if (el) {
          el.scrollTop = previousTop + (el.scrollHeight - previousHeight)
        }
      } catch (e) {
        // Al volver a subir lo intenta de nuevo.
      } finally {
        this.loadingOlder = false
      }
    },
    async sendOwnMessage(local) {
      try {
        const saved = await this.storeOwnMessage(local)
        this.messages = confirmMessage(this.messages, local.uuid, saved)
      } catch (err) {
        this.messages = updateLocal(this.messages, local.uuid, {
          status: 'failed',
          failure: sendFailure(err),
        })
      }
    },
    retryMessage(row) {
      const local = this.messages.find((message) => message.uuid === row.uuid)
      if (!local) {
        return
      }
      this.messages = updateLocal(this.messages, local.uuid, {
        status: 'sending',
        failure: null,
      })
      this.sendOwnMessage(local)
    },
    discardMessage(row) {
      this.messages = this.messages.filter((message) => message.uuid !== row.uuid)
    },
  },
}
